'use client'

import { createContext, useContext, useEffect, useMemo, useRef } from 'react'

import { logEvent } from '@/shared/external/firebase'

import { RANK_EVENT, RANK_TRACK_CODE } from '../_constants'

import type { ReactNode } from 'react'

type TTrackParams = Record<string, string | number | boolean | null>

export interface IRankCommonParams {
  rank_type: string
  category: string
  mode: string
}

export interface IRankTracker {
  track: (eventName: string, params?: TTrackParams) => void
  pageShow: () => void
  sectionShow: (code: string, label: string) => void
  itemShow: (
    code: string,
    type: string,
    label: string,
    extra?: TTrackParams
  ) => void
  itemClick: (
    code: string,
    type: string,
    label: string,
    extra?: TTrackParams
  ) => void
  itemChange: (
    code: string,
    type: string,
    label: string,
    extra?: TTrackParams
  ) => void
  logoClick: () => void
}

const createRankTracker = (common: IRankCommonParams): IRankTracker => {
  const track = (eventName: string, params: TTrackParams = {}) => {
    logEvent(eventName, { ...params, ...common })
  }

  return {
    track,
    pageShow: () =>
      track(RANK_EVENT.PAGE_SHOW, {
        code: RANK_TRACK_CODE.PAGE,
        type: 'page',
        label: '모두의 차고 랭킹 리스트 조회',
      }),
    sectionShow: (code, label) =>
      track(RANK_EVENT.SECTION_SHOW, { code, type: 'section', label }),
    itemShow: (code, type, label, extra = {}) =>
      track(RANK_EVENT.ITEM_SHOW, { code, type, label, ...extra }),
    itemClick: (code, type, label, extra = {}) =>
      track(RANK_EVENT.ITEM_CLICK, { code, type, label, ...extra }),
    itemChange: (code, type, label, extra = {}) =>
      track(RANK_EVENT.ITEM_CHANGE, { code, type, label, ...extra }),
    logoClick: () =>
      track(RANK_EVENT.LOGO_CLICK, {
        code: RANK_TRACK_CODE.HEADER,
        type: 'logo',
        label: '마이클 로고 클릭',
      }),
  }
}

const NOOP_TRACKER: IRankTracker = {
  track: () => {},
  pageShow: () => {},
  sectionShow: () => {},
  itemShow: () => {},
  itemClick: () => {},
  itemChange: () => {},
  logoClick: () => {},
}

const RankTrackingContext = createContext<IRankTracker | null>(null)

export const useRankTracking = (common: IRankCommonParams): IRankTracker =>
  useMemo(() => createRankTracker(common), [common])

interface IRankTrackingProviderProps {
  value: IRankTracker
  children: ReactNode
}

export const RankTrackingProvider = ({
  value,
  children,
}: IRankTrackingProviderProps) => (
  <RankTrackingContext.Provider value={value}>
    {children}
  </RankTrackingContext.Provider>
)

export const useRankTrackingContext = (): IRankTracker =>
  useContext(RankTrackingContext) ?? NOOP_TRACKER

export const useRankImpression = <T extends HTMLElement = HTMLDivElement>(
  onImpress: () => void,
  options?: { enabled?: boolean; threshold?: number }
) => {
  const ref = useRef<T | null>(null)
  const firedRef = useRef(false)
  const callbackRef = useRef(onImpress)
  callbackRef.current = onImpress

  const enabled = options?.enabled ?? true
  const threshold = options?.threshold ?? 0.5

  useEffect(() => {
    if (!enabled || firedRef.current || !ref.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true
          callbackRef.current()
          observer.disconnect()
        }
      },
      { threshold }
    )

    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [enabled, threshold])

  return ref
}
